import type { ReactNode } from "react"
import { AlertTriangle, Ban, CheckCircle2, Info, Loader2 } from "lucide-react"
import { format } from "date-fns"
import { Label } from "@/components/ui/label"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/translations"
import type {
  PreVerificationDecision,
  PreVerificationPhase,
  PreVerificationState,
} from "@/api/onboarding-api.types"
import { DATE_FORMAT, FIELD_ANCHOR_ID_PREFIX } from "../constants"

export interface PreVerificationFieldProps {
  state: PreVerificationState
  decision: PreVerificationDecision | null
  onDecisionChange: (decision: PreVerificationDecision | null) => void
  error?: string
  isRefreshing: boolean
  onRefresh: () => void
  onView: () => void
  /** True while the form is being updated with the values approved after it opened. */
  isUpdatingFromReview: boolean
  /** True when that update failed or did not bring the reviewed values in yet. */
  updateFromReviewFailed: boolean
  onRetryUpdateFromReview: () => void
  /** True once the form was updated with the reviewed values. */
  wasUpdatedFromReview: boolean
}

type FinishedPhase = "Approved" | "Discarded" | "Cancelled"

const STATUS_KEYS: Record<Exclude<PreVerificationPhase, "Unavailable" | FinishedPhase>, TranslationKey> = {
  Draft: "preVerificationStatusDraft",
  NotSent: "preVerificationStatusNotSent",
  WithWorker: "preVerificationStatusWithWorker",
  OnHold: "preVerificationStatusOnHold",
  Submitted: "preVerificationStatusSubmitted",
  RequiresReview: "preVerificationStatusRequiresReview",
  ApprovedSinceOpened: "preVerificationStatusApproved",
}

const FINISHED_LINES: Record<FinishedPhase, { dated: TranslationKey; undated: TranslationKey; completed: boolean }> = {
  Approved: { dated: "preVerificationCompletedOn", undated: "preVerificationCompleted", completed: true },
  Discarded: { dated: "preVerificationDiscardedOn", undated: "preVerificationDiscarded", completed: false },
  Cancelled: { dated: "preVerificationCancelledOn", undated: "preVerificationCancelled", completed: false },
}

/**
 * The linked pre-verification: what the reviewer has to confirm, why Proceed is blocked, or, once it is completed,
 * discarded or cancelled, a line saying so and when.
 */
export function PreVerificationField({
  state,
  decision,
  onDecisionChange,
  error,
  isRefreshing,
  onRefresh,
  onView,
  isUpdatingFromReview,
  updateFromReviewFailed,
  onRetryUpdateFromReview,
  wasUpdatedFromReview,
}: PreVerificationFieldProps) {
  const { t } = useTranslation()
  const finishedLine = state.phase in FINISHED_LINES ? FINISHED_LINES[state.phase as FinishedPhase] : null
  const statusKey = finishedLine || state.phase === "Unavailable" ? null : STATUS_KEYS[state.phase as keyof typeof STATUS_KEYS]
  // A client request offers no way to continue without its status: Proceed waits for a Retry that reads it.
  const canContinueUnchecked = state.allowedDecisions.includes("ContinueUnchecked")

  const withDiscardedHighlighted = (key: TranslationKey) => {
    const [before, after = ""] = t(key).split("{discarded}")
    return (
      <>
        {before}
        <span className="font-semibold text-red-700">{t("preVerificationDiscardedWord")}</span>
        {after}
      </>
    )
  }

  const decisionCheckbox = (value: PreVerificationDecision, labelKey: TranslationKey, help?: ReactNode) => (
    <>
      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          className="mt-0.5 shrink-0"
          checked={decision === value}
          onChange={(e) => onDecisionChange(e.target.checked ? value : null)}
        />
        {t(labelKey)}
      </label>
      {error ? <p className="text-xs text-red-500">{error}</p> : decision === value ? help : null}
    </>
  )

  const blockedBanner = (messageKey: TranslationKey) => (
    <div className="rounded-lg bg-red-50 border border-red-300 p-3.5 flex gap-3">
      <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
      <p className="text-xs text-red-800 leading-relaxed">{t(messageKey)}</p>
    </div>
  )

  return (
    <div id={`${FIELD_ANCHOR_ID_PREFIX}preVerification`} className="flex flex-col gap-1.5 mb-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-sm font-medium text-gray-700">
          {t("labelPreVerification")}
          <span className="text-red-500 ml-0.5">*</span>
        </Label>
        {statusKey && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ring-1 ring-inset bg-amber-50 text-amber-700 ring-amber-600/20">
            {t(statusKey)}
          </span>
        )}
        {state.phase !== "Unavailable" && (
          <span className="ml-auto flex items-center gap-3">
            <button
              type="button"
              className="text-xs font-medium text-blue-600 hover:text-blue-700 underline underline-offset-2"
              onClick={onView}
            >
              {t("viewPreVerification")}
            </button>
            {!finishedLine && state.phase !== "ApprovedSinceOpened" && (
              <span className="relative group">
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  className="text-xs font-medium px-2 py-0.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {t("preVerificationRefresh")}
                </button>
                <span
                  role="tooltip"
                  className="absolute top-full right-0 mt-1.5 w-max max-w-xs px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10"
                >
                  {t("preVerificationRefreshHint")}
                </span>
              </span>
            )}
          </span>
        )}
      </div>

      {finishedLine && (
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-md border ${
            finishedLine.completed ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"
          }`}
        >
          {finishedLine.completed ? (
            <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
          ) : (
            <Ban size={14} className="text-gray-500 shrink-0" />
          )}
          <div className="flex flex-col gap-0.5">
            <p className={`text-xs ${finishedLine.completed ? "text-emerald-800" : "text-gray-700"}`}>
              {state.finishedAt
                ? t(finishedLine.dated, { date: format(new Date(state.finishedAt), DATE_FORMAT) })
                : t(finishedLine.undated)}
            </p>
            {state.phase === "Approved" && wasUpdatedFromReview && (
              <p className="text-xs text-emerald-800">{t("preVerificationUpdatedFromReview")}</p>
            )}
          </div>
        </div>
      )}

      {(state.phase === "Draft" || state.phase === "WithWorker" || state.phase === "OnHold")
        && decisionCheckbox(
          "Discard",
          "preVerificationDiscardCheckbox",
          <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-300">
            <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">{withDiscardedHighlighted("preVerificationDiscardHelp")}</p>
          </div>,
        )}

      {state.phase === "NotSent" && (
        <>
          <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-blue-50 border border-blue-300">
            <Info size={14} className="text-blue-600 shrink-0 mt-0.5" />
            <p className="flex-1 text-xs text-blue-800 leading-relaxed">{t("preVerificationNotSentHelp")}</p>
          </div>
          {decisionCheckbox(
            "Discard",
            "preVerificationDiscardInsteadCheckbox",
            <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-300">
              <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">{withDiscardedHighlighted("preVerificationDiscardRequestHelp")}</p>
            </div>,
          )}
        </>
      )}

      {state.phase === "Submitted" && blockedBanner("preVerificationAwaitingReview")}

      {state.phase === "RequiresReview" && blockedBanner("preVerificationReturnedForReview")}

      {state.phase === "ApprovedSinceOpened" && (
        <>
          <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-blue-50 border border-blue-300">
            <Info size={14} className="text-blue-600 shrink-0 mt-0.5" />
            {updateFromReviewFailed ? (
              <>
                <p className="flex-1 text-xs text-blue-800 leading-relaxed">{t("preVerificationUpdateFromReviewFailed")}</p>
                <button
                  type="button"
                  onClick={onRetryUpdateFromReview}
                  disabled={isUpdatingFromReview}
                  className="shrink-0 text-xs font-medium px-2.5 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {t("preVerificationRetry")}
                </button>
              </>
            ) : (
              <>
                <p className="flex-1 text-xs text-blue-800 leading-relaxed">{t("preVerificationUpdatingFromReview")}</p>
                {isUpdatingFromReview && (
                  <Loader2 size={14} className="animate-spin text-blue-600 shrink-0 mt-0.5" />
                )}
              </>
            )}
          </div>
          {updateFromReviewFailed
            && decisionCheckbox("ContinueWithoutReview", "preVerificationContinueWithoutReviewCheckbox")}
        </>
      )}

      {state.phase === "Unavailable" && (
        <>
          <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-300">
            <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="flex-1 text-xs text-amber-800 leading-relaxed">
              {t(canContinueUnchecked ? "preVerificationUnavailable" : "preVerificationUnavailableBlocked")}
            </p>
            <button
              type="button"
              onClick={onRefresh}
              disabled={isRefreshing}
              className="shrink-0 text-xs font-medium px-2.5 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t("preVerificationRetry")}
            </button>
          </div>
          {canContinueUnchecked && decisionCheckbox("ContinueUnchecked", "preVerificationContinueUncheckedCheckbox")}
        </>
      )}
    </div>
  )
}
